import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Alert,
    useTheme,
    alpha,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider
} from '@mui/material';
import {
    Calendar,
    Users,
    AlertTriangle,
    Building2,
    DollarSign
} from 'lucide-react';

const CardBillingConfirmationDialog = ({
                                           isOpen,
                                           onConfirm,
                                           onCancel,
                                           billingData
                                       }) => {
    const theme = useTheme();

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onCancel}
            maxWidth="lg"
            fullWidth
            sx={{
                zIndex: theme => theme.zIndex.modal + 100, // Ensures it's above other dialogs
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }
            }}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    minWidth: '800px'
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    background: 'linear-gradient(45deg, #2563eb, #4338ca)',
                    color: 'white',
                    p: 3
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <AlertTriangle size={24} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            Billing Confirmation
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Review details before proceeding
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: 3 }}>
                {/* Billing Summary */}
                <Box
                    sx={{
                        backgroundColor: '#f9fafb',
                        borderRadius: 1.5,
                        p: 2,
                        mb: 3
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
                        Billing Summary
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Building2 size={16} color="#6b7280" />
                                    <Typography variant="body2" color="text.secondary">
                                        Property
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {billingData.propertyName || 'N/A'}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={6}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Calendar size={16} color="#6b7280" />
                                    <Typography variant="body2" color="text.secondary">
                                        Period
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {billingData.month} {billingData.year}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={6}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Users size={16} color="#6b7280" />
                                    <Typography variant="body2" color="text.secondary">
                                        Tenants
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {billingData.tenantCount || 0} units
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={6}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <DollarSign size={16} color="#6b7280" />
                                    <Typography variant="body2" color="text.secondary">
                                        Est. Total
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                    {formatCurrency(billingData.estimatedTotal || 0)}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Tenant Billing Details Table */}
                {billingData.tenantBills && billingData.tenantBills.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
                            Billing Breakdown
                        </Typography>
                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: 400,
                                boxShadow: 'none',
                                border: '1px solid #e5e7eb',
                                borderRadius: 1.5
                            }}
                        >
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }}>Tenant</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }} align="right">Unit</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }} align="right">Rent</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }} align="right">Water</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }} align="right">Electricity</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }} align="right">Outstanding</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }} align="center">Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', color: '#2563eb' }} align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {billingData.tenantBills.map((bill, index) => {
                                        const isPending = bill.status === "PENDING";
                                        return (
                                            <TableRow key={index} hover>
                                                <TableCell>{bill.tenantName}</TableCell>
                                                <TableCell align="right">{bill.houseNumber}</TableCell>
                                                <TableCell align="right">{formatCurrency(bill.rent || 0)}</TableCell>
                                                <TableCell align="right">{formatCurrency(bill.water_bill || 0)}</TableCell>
                                                <TableCell align="right">{formatCurrency(bill.electricity_bill || 0)}</TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color: isPending ? '#dc2626' : '#16a34a',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {formatCurrency(bill.arrears || 0)}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        color: isPending ? '#dc2626' : '#16a34a',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    {isPending ? 'PENDING' : 'PAID'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, color: '#2563eb' }}>
                                                    {formatCurrency(bill.total_bill || 0)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                        <TableCell colSpan={7} sx={{ fontWeight: 700 }}>GRAND TOTAL</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: '#2563eb', fontSize: '1.1rem' }}>
                                            {formatCurrency(billingData.estimatedTotal || 0)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Warning Alert */}
                <Alert
                    severity="warning"
                    icon={<AlertTriangle size={20} />}
                    sx={{
                        mb: 3,
                        borderRadius: 1,
                        '& .MuiAlert-message': {
                            width: '100%'
                        }
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Important Notice
                    </Typography>
                    <Typography variant="body2">
                        This will generate bills and send SMS notifications to all tenants. This action cannot be undone.
                    </Typography>
                </Alert>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ p: 3, gap: 1.5 }}>
                <Button
                    onClick={onCancel}
                    variant="outlined"
                    color="inherit"
                    sx={{
                        flex: 1,
                        py: 1.5,
                        borderColor: '#d1d5db',
                        color: '#374151',
                        '&:hover': {
                            backgroundColor: '#f9fafb',
                            borderColor: '#d1d5db'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    sx={{
                        flex: 1,
                        py: 1.5,
                        backgroundColor: '#2563eb',
                        '&:hover': {
                            backgroundColor: '#1d4ed8'
                        }
                    }}
                >
                    Proceed with Billing
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CardBillingConfirmationDialog;